import { Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AppHeader } from './components/AppHeader';
import { AppFooter } from './components/AppFooter';
import { Callout } from './components/Callout';
import { useI18n } from './i18n';
import { apiConfigured, firebaseConfigured } from './config/env';
import { HomeRoute } from './routes/HomeRoute';
import { ResultRoute } from './routes/ResultRoute';
import { SavedMapsRoute } from './routes/SavedMapsRoute';
import { PublicMapRoute } from './routes/PublicMapRoute';
import { NotFoundRoute } from './routes/NotFoundRoute';

/**
 * Two shells. The scrolling one is for the forms and the history; the full one
 * gives the map every pixel below the header and lets only the side column
 * scroll.
 */
function Shell({ children, full = false }: { children: ReactNode; full?: boolean }) {
  const { t } = useI18n();
  return (
    <div className={full ? 'app app--full' : 'app'}>
      <a className="skip-link" href="#main">
        {t('app.skipToContent')}
      </a>
      <AppHeader compact={full} />
      <main className="main" id="main">
        {children}
      </main>
      {full ? null : <AppFooter />}
    </div>
  );
}

/** Shown when a deployment is missing its build-time configuration. Better
 *  than a blank screen and a console error nobody reads. */
function ConfigurationNotice() {
  const { t } = useI18n();
  const missing: string[] = [];
  if (!apiConfigured) missing.push('VITE_API_BASE_URL');
  if (!firebaseConfigured) missing.push('VITE_FIREBASE_*');
  if (missing.length === 0) return null;

  return (
    <div className="page" style={{ paddingBottom: 0 }}>
      <Callout tone="error">
        <p>{t('error.notConfigured', { name: missing.join(', ') })}</p>
      </Callout>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Shell>
            <ConfigurationNotice />
            <HomeRoute />
          </Shell>
        }
      />
      <Route
        path="/maps"
        element={
          <Shell>
            <SavedMapsRoute />
          </Shell>
        }
      />
      <Route
        path="/maps/:mapId"
        element={
          <Shell full>
            <ResultRoute />
          </Shell>
        }
      />
      <Route
        path="/m/:shareId"
        element={
          <Shell full>
            <PublicMapRoute />
          </Shell>
        }
      />
      <Route
        path="*"
        element={
          <Shell>
            <NotFoundRoute />
          </Shell>
        }
      />
    </Routes>
  );
}
